import React, { useState, useEffect } from 'react';
import { trim, eq } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useMutation } from '@apollo/client';

import { Toggle } from '../inputControls';
import { GENERAL_SETTINGS, UPDATE_GENERAL_SETTINGS } from '../../../graphqls';
import Section from './section';

export default function SystemSettings() {
  const [googleTracking, setGoogleTracking] = useState('');
  const [translateProvider, setTranslateProvider] = useState('NONE');
  const [isA11yMode, setA11yMode] = useState(false);
  const [customSubDomain, setCustomSubDomain] = useState('');
  const [logoLink, setLogoLink] = useState('');
  const [submitNotification, setSubmitNotification] = useState('');
  const [enableIpList, setEnableIpList] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [subdomainWarning, setSubdomainWarning] = useState('');
  const [ipWarning, setIpWarning] = useState('');
  const [ipInput, setIpInput] = useState([]);

  const { loading, data = {} } = useQuery(GENERAL_SETTINGS);
  const [updateGeneralSettings] = useMutation(UPDATE_GENERAL_SETTINGS);

  useEffect(() => {
    if (data.generalSettings) {
      const { generalSettings } = data;

      setGoogleTracking(generalSettings.googleTracking);
      setTranslateProvider(generalSettings.translateProvider ?? 'NONE');
      setA11yMode(generalSettings.isA11yMode);
      setTranslateProvider(generalSettings.translateProvider ?? 'NONE');
      setCustomSubDomain(generalSettings.customSubDomain);
      setLogoLink(generalSettings.logoLink);
      setEnableIpList(generalSettings.enableIpList);
      setIpInput(generalSettings.ipWhiteList);
    }
  }, [data]);

  useEffect(() => {
    setDisableSubmit(!!(subdomainWarning || ipWarning));
  }, [subdomainWarning, ipWarning]);

  if (loading) {
    return '';
  }

  const handleRemoveIp = ({
    target: {
      dataset: { index },
    },
  }) => setIpInput(ipInput.splice(index, 1));
  const handleEnableIpList = () => {
    if (eq(enableIpList, false)) {
      if (ipInput.length <= 0 || !ipInput[0]) {
        setIpWarning('Ip white listing enabled, but no IP entered. (x.x.x.x).');
      } else {
        const lastIp = trim(ipInput[0]);

        if (lastIp) {
          const passable = ipInput.every((ip) =>
            /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
              ip,
            ),
          );

          if (passable === false) {
            setIpWarning('Incorrect IP address format. (x.x.x.x).');
          }
        } else {
          setIpWarning('Incorrect IP address format. (x.x.x.x).');
        }
      }
    } else {
      setIpWarning('');
    }

    setEnableIpList(!enableIpList);
  };
  const handleIpInputChange = ({
    target: {
      value,
      dataset: { index },
    },
  }) => {
    const newIpInput = JSON.parse(JSON.stringify(ipInput));
    newIpInput[index] = value;

    setIpInput(newIpInput);

    const passable = newIpInput.every((ip) =>
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
        ip,
      ),
    );

    setIpWarning(passable ? '' : 'Incorrect IP address format. (x.x.x.x)');
  };
  const onSubmit = () => {
    if (ipInput.some((item) => !item) && enableIpList) {
      setIpWarning('Incorrect IP address format. (x.x.x.x)');
    }

    updateGeneralSettings({
      variables: {
        ipWhiteList: ipInput,
        googleTracking,
        translateProvider,
        isA11yMode,
        translateProvider,
        customSubDomain,
        enableIpList,
        logoLink,
      },
    })
      .then(() => setSubmitNotification('Successfully submitted new settings.'))
      .catch((error) => {
        console.error(error);

        setSubmitNotification('Custom SubDomain already existed.');
      });

    setTimeout(() => setSubmitNotification(''), 3000);
  };
  const handleAddIp = () => {
    setIpInput([...ipInput, '']);
    setIpWarning('Incorrect IP address format. (x.x.x.x).');
  };
  const onSubDomainChange = ({ target: { value } }) => {
    setCustomSubDomain(value);
    setSubdomainWarning(value.match(/[^\w-]+/) ? 'Custom subdomain cannot be empty. Valid values are A-z, 0-9, and "-", "_".' : '');
  };
  const handleLogoLinkInput = ({ target: { value } }) => setLogoLink(value);
  const handleTrackingInput = ({ target: { value } }) => setGoogleTracking(value);
  const onSwitchTranslateProvider = ({ target: { value } }) => setTranslateProvider(value);
  const onToggleA11yMode = () => setA11yMode(!isA11yMode);
  let ipList;
  let addIpBtn;

  if (enableIpList) {
    ipList = ipInput.map((ip, index) => {
      const removeBtn =
        ipInput.length > 1 ? (
          <button type="button" className="hollow-btn" data-index={index} onClick={handleRemoveIp}>
            <FontAwesomeIcon icon={['fal', 'times']} />
          </button>
        ) : null;
      const ipKey = `${ip}${index}`;

      return (
        <div className="form-group" key={ipKey}>
          <input
            name="ipAddress"
            type="text"
            className="form-control"
            placeholder="enter allowed IP address"
            defaultValue={ip}
            data-index={index}
            onChange={handleIpInputChange}
          />
          {removeBtn}
        </div>
      );
    });
    addIpBtn = (
      <div className="add-ip-address-container">
        <button type="button" className="hollow-btn add-ip-address" onClick={handleAddIp}>
          + Add IP Address
        </button>
      </div>
    );
  }
  const translateConfig = {
    label: 'Translator Widget',
    select: {
      className: 'number-of-columns',
      onChange: onSwitchTranslateProvider,
      value: translateProvider,
      options: [
        { label: 'None', value: 'NONE' },
        { label: 'Elfsight', value: 'ELFSIGHT' },
        // { label: 'Google', value: 'GOOGLE' },
      ],
    },
  };

  return (
    <div className="system-settings general-settings">
      <strong className="general-setting-label">System Settings</strong>
      <div className="form-group">
        <label htmlFor="customSubDomain">
          Custom SubDomain
          <input
            name="customSubDomain"
            placeholder="Enter Your Custom SubDomain"
            type="text"
            className="form-control"
            value={customSubDomain}
            onChange={onSubDomainChange}
          />
        </label>
      </div>
      <span className="error">{subdomainWarning}</span>
      <div className="form-group">
        <label htmlFor="logoLink">
          Custom Logo Link
          <input
            name="logoLink"
            placeholder={`https://performance.envisio.com/dashboard/${customSubDomain}`}
            type="text"
            className="form-control"
            value={logoLink}
            onChange={handleLogoLinkInput}
          />
        </label>
      </div>
      <div className="form-group">
        <label htmlFor="googleAnalytics">
          Google Analytics Tracking Code
          <input
            name="googleAnalytics"
            placeholder="enter your tracking code"
            type="text"
            className="form-control"
            value={googleTracking}
            onChange={handleTrackingInput}
          />
        </label>
      </div>
      <Section config={translateConfig} customClass="translator-widget" />
      <div className="form-group ip-addresses-container">
        <label htmlFor="a11yMode">
          Enable Enhanced Accessibility
          <Toggle checked={isA11yMode} onChange={onToggleA11yMode} />
        </label>
      </div>
      <div className="form-group ip-addresses-container">
        <label htmlFor="ipAddress">
          IP Whitelist
          <Toggle checked={enableIpList} onChange={handleEnableIpList} />
          <div className="ip-whitelist-note">Use IP Whitelist to limit and control access of your published url only to trusted IPs</div>
          <div className="ip-address">{ipList}</div>
        </label>
        {addIpBtn}
      </div>
      <span className="error">{ipWarning}</span>
      <div className="button-container">
        <button type="button" className="btn mint-btn submit-btn" disabled={disableSubmit} onClick={onSubmit}>
          Submit
        </button>
      </div>
      {submitNotification}
    </div>
  );
}
